import { Box, Link, Typography, IconButton, useTheme } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import Container from '../common/ui/Container';
import ROUTES from '../../config/routes';

/**
 * Footer component with company information and copyright
 *
 * @returns {React.Component} FooterComponent
 */
const FooterComponent = () => {
  const location = useLocation();
  const theme = useTheme();

  // Don't render footer on home page (controlled by Layout component)
  if (location.pathname === '/') {
    return null;
  }

  // Calculate full-width extension values
  const footerSx = {
    width: {
      xs: `calc(100% + ${theme.spacing(4)})`,
      sm: `calc(100% + ${theme.spacing(6)})`,
      md: '100%'
    },
    ml: { xs: -2, sm: -3, md: 0 },
    mr: { xs: -2, sm: -3, md: 0 },
    overflowX: 'hidden',
    zIndex: 5,
    mt: { xs: 4, md: 6 },
    background: 'linear-gradient(135deg, rgba(131,9,254,0.04) 0%, rgba(4,203,255,0.04) 40%, rgba(109,0,119,0.03) 100%)',
    borderTop: 1,
    borderLeft: 1,
    borderRight: 1,
    borderColor: 'divider'
  };

  return (
    <Box
      component='footer'
      sx={footerSx}
    >
      <Container maxWidth='xl' py={{ xs: 3, md: 4 }} px={{ xs: 2, md: 4 }} disableGutters={false}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'center', md: 'center' },
            justifyContent: { xs: 'center', md: 'space-between' },
            gap: { xs: 2.5, md: 3 },
            textAlign: { xs: 'center', md: 'left' }
          }}
        >
          {/* Address and copyright */}
          <Box sx={{ maxWidth: { xs: '100%', md: '60%' } }}>
            <Typography variant='body2' sx={{ color: 'text.primary', lineHeight: 1.6 }}>
              Capital Office, 124 City Road
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.primary', mb: 1, lineHeight: 1.6 }}>
              London, EC1V 2NX, England
            </Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              COPYRIGHT © 2025 Spinnaker Algo Systems Limited | ALL RIGHTS RESERVED
            </Typography>
          </Box>

          {/* Navigation + Socials */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-end' }, gap: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2.4 }, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
              {[
                { label: 'About', to: ROUTES.ABOUT_US },
                { label: 'SwingVision', to: ROUTES.SWING_VISION },
                { label: 'Q Alpha Flow', to: ROUTES.Q_ALPHA_FLOW },
                { label: 'Octopus Sync', to: ROUTES.OCTOPUS_SYNC },
                { label: 'Sentinel', to: ROUTES.SENTINEL },
                { label: 'Contact', to: ROUTES.CONTACTS },
              ].map((item) => (
                <Link
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  color='text.primary'
                  underline='none'
                  sx={{
                    position: 'relative',
                    fontFamily: 'Manrope, "Nunito Sans", sans-serif',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    fontSize: '0.95rem',
                    textDecoration: 'none !important',
                    transition: 'color 150ms ease, transform 180ms ease',
                    '&:hover': { color: 'primary.main', transform: 'translateY(-2px)' },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      bottom: -4,
                      width: '100%',
                      height: 2,
                      backgroundColor: 'primary.main',
                      opacity: 0,
                      transform: 'scaleX(0.6)',
                      transition: 'opacity 180ms ease, transform 180ms ease',
                    },
                    '&:hover::after': {
                      opacity: 1,
                      transform: 'scaleX(1)',
                    },
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Box>
            <IconButton
              component={Link}
              href='https://www.linkedin.com/company/spinnaker-algo-ltd/'
              target='_blank'
              rel='noopener noreferrer'
              aria-label='LinkedIn'
              sx={{
                color: 'primary.main',
                '&:hover': { opacity: 0.85, transform: 'translateY(-1px)' },
                transition: 'transform 180ms ease, opacity 180ms ease'
              }}
            >
              <LinkedInIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default FooterComponent;